import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User } from "lucide-react";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useToast } from "@/hooks/use-toast";
import ProfileForm from "@/components/forms/ProfileForm";
import ProfileOnboarding from "@/components/onboarding/ProfileOnboarding";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { UserProfileData } from "@/types/dashboard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserDetails, useUpdateUserProfile } from "@/hooks/api/useUserDetails";
import { Label } from "@/components/ui/label";
import { useAnalytics } from "@/hooks/useAnalytics";
import { NotificationSettings } from "@/components/settings/NotificationSettings";

// Default user profile for new users
const defaultUserProfile: UserProfileData = {
  name: "",
  email: "",
  photoUrl: "",
  country: "",
  contactSyncEnabled: false,
  calendarSyncSettings: {
    enabled: false,
    syncBirthdays: true,
    syncAnniversaries: true,
    syncCustomEvents: false,
  },
  onboardingCompleted: false,
  reminderLeadTime: 7,
  preferredReminderTime: "9:00 AM",
  reminderChannels: ["email", "app"],
  trackedContacts: 0,
  remindersSet: 0,
  lastLoginDate: new Date(),
  averageCheckInInterval: 0,
  syncedContactSources: [],
};

const AVAILABLE_AVATARS = [
  'm1.png',
  'm2.png',
  'm3.png',
  'f1.png',
  'f2.png',
  'f3.png',
];

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { trackFormSubmission } = useAnalytics();
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);

  // Check if onboarding is needed and get any user data from URL params
  const searchParams = new URLSearchParams(location.search);
  const isOnboarding = searchParams.get("onboarding") === "true";
  const nameFromUrl = searchParams.get("name");
  const emailFromUrl = searchParams.get("email");

  // Fetch user details from API
  const { data: userDetails, isLoading, error: userError } = useUserDetails();
  const updateUserProfile = useUpdateUserProfile();

  // Convert API user details to profile data format
  useEffect(() => {
    if (userDetails) {
      // Map API data to local profile data structure
      const userProfileData: UserProfileData = {
        ...defaultUserProfile,
        name: `${userDetails.first_name} ${userDetails.last_name}`.trim() || userDetails.email,
        email: userDetails.email,
        photoUrl: userDetails.avatar || "",
        phoneNumber: userDetails.phone,
        country: userDetails.country,
        onboardingCompleted: userDetails.completedOnboarding,
        // Note: Some fields from defaultUserProfile don't exist in API yet
        // (like timezone, social handles, sync settings)
        // Keep defaults for those
      };

      setProfileData(userProfileData);
    } else if (isOnboarding) {
      // For onboarding, use minimal data from URL params
      const userProfileData = {
        ...defaultUserProfile,
        name: nameFromUrl || "",
        email: emailFromUrl || "",
      };
      setProfileData(userProfileData);
    }
  }, [userDetails, isOnboarding, nameFromUrl, emailFromUrl]);

  // Handle API errors
  useEffect(() => {
    if (userError && !isOnboarding) {
      console.error("Error loading profile data:", userError);
      toast({
        title: "Error Loading Profile",
        description: "There was a problem loading your profile data.",
        variant: "destructive",
      });
    }
  }, [userError, isOnboarding, toast]);
  
  const handleSaveProfile = async (data: UserProfileData) => {
    setProfileData(data);

    try {
      // Prepare update payload for API
      const updatePayload = {
        name: data.name,
        phone: data.phoneNumber,
        country: data.country,
        // Add onboarding completion if this is onboarding flow
        ...(isOnboarding && { onboarding_step: 999 }), // Use high number to indicate completion
      };

      // Call API to update profile
      await updateUserProfile.mutateAsync(updatePayload);

      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });

      // If this was part of onboarding, redirect to dashboard
      if (isOnboarding) {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error Saving Profile",
        description: "There was a problem saving your profile data.",
        variant: "destructive",
      });
    }
  };
  
  const handleOnboardingComplete = (data: UserProfileData) => {
    handleSaveProfile(data);
  };

  const handleAvatarSelect = async (avatar: string) => {
    if (profileData) {
      const updatedProfile = {
        ...profileData,
        photoUrl: avatar,
      };
      setProfileData(updatedProfile);

      try {
        // Save avatar immediately
        await updateUserProfile.mutateAsync({ avatar });

        toast({
          title: "Avatar Updated",
          description: "Your profile picture has been updated successfully.",
        });
      } catch (error) {
        console.error("Error updating avatar:", error);
        toast({
          title: "Error",
          description: "Failed to update avatar. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  // For the initial loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-4">
          <div className="container mx-auto px-4 pt-4">
            <div className="animate-pulse flex flex-col space-y-4">
              <div className="h-10 bg-gray-200 rounded w-1/4"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isOnboarding) {
    return (
      <DashboardLayout>
        <div className="pb-10">
          <div className="container mx-auto px-4 pt-4">
            <h1 className="mb-6 text-2xl font-bold">Complete Your Profile</h1>
            
            <div className="mx-auto max-w-2xl">
              <ProfileOnboarding 
                onComplete={handleOnboardingComplete}
                initialData={{
                  name: nameFromUrl || profileData?.name || '',
                  email: emailFromUrl || profileData?.email || '',
                  ...(profileData || {}),
                }}
              />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Full profile view
  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Profile Content - Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6">
            {/* Left Side - Form */}
            <div className="bg-white rounded-lg p-8">
              {profileData && (
                <ProfileForm
                  key={profileData.email}
                  initialData={profileData}
                  onSave={handleSaveProfile}
                  selectedAvatar={profileData.photoUrl}
                  onAvatarSelect={handleAvatarSelect}
                />
              )}
            </div>

            {/* Right Side - Preview Card */}
            {profileData && (
              <div className="bg-white rounded-lg p-6 w-full lg:w-[380px] h-fit">
                <h4 className="text-[20px] font-bold text-wabisabi-plum mb-6">
                  Profile Preview
                </h4>

                {/* Avatar and Name */}
                <div className="flex flex-col items-center mb-6">
                  <Avatar className="w-20 h-20 mb-4">
                    {profileData?.photoUrl ? (
                      <AvatarImage
                        src={profileData.photoUrl.startsWith('/') || profileData.photoUrl.startsWith('http')
                          ? profileData.photoUrl
                          : `/images/avatars/${profileData.photoUrl}`
                        }
                        alt={profileData?.name || "User"}
                      />
                    ) : null}
                    <AvatarFallback className="bg-gray-200">
                      <User className="w-10 h-10 text-gray-500" />
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-[16px] font-bold text-black">
                    {profileData?.name || "No name yet"}
                  </p>
                </div>

                {/* Profile Details */}
                <div>
                  {/* Email */}
                  <div className="flex justify-between items-start pb-4 border-b border-gray-200">
                    <span className="text-[14px] text-[#8E8E8E]">Email</span>
                    <span className="text-[14px] text-black text-right break-all max-w-[60%]">
                      {profileData?.email || "-"}
                    </span>
                  </div>

                  {/* Phone */}
                  <div className="flex justify-between items-start py-4 border-b border-gray-200">
                    <span className="text-[14px] text-[#8E8E8E]">Phone</span>
                    <span className="text-[14px] text-black text-right">
                      {profileData?.phoneNumber || "-"}
                    </span>
                  </div>

                  {/* Country */}
                  <div className="flex justify-between items-start py-4 border-b border-gray-200">
                    <span className="text-[14px] text-[#8E8E8E]">Country</span>
                    <span className="text-[14px] text-black text-right">
                      {profileData?.country || "-"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notification Settings Section */}
          <div className="mt-6">
            <NotificationSettings />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
